'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CategoryItem } from '@/types/pdp';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';

interface Props {
  descEn: string;
  descRu: string;
  descUk?: string | null;
  shortDescEn?: string | null;
  shortDescRu?: string | null;
  shortDescUk?: string | null;
  lang?: string;
  categories: CategoryItem[];
}

const GLASS: React.CSSProperties = {
  borderRadius: 16,
  border: '1px solid var(--v4-border)',
  background: 'var(--v4-panel)',
  backdropFilter: 'blur(20px)',
  padding: 20,
};

function SecTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 3, height: 16, background: 'var(--gs-green)', borderRadius: 2, display: 'block', flexShrink: 0 }} />
      {children}
    </div>
  );
}


export function AboutSection({ descEn, descRu, descUk, shortDescEn, shortDescRu, shortDescUk, categories }: Props) {
  const { lang } = usePriceConvert();
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  const desc = lang === 'ru' ? (descRu || descEn)
    : lang === 'uk' ? (descUk || descEn)
    : descEn;

  const shortDesc = lang === 'ru' ? (shortDescRu || shortDescEn || null)
    : lang === 'uk' ? (shortDescUk || shortDescEn || null)
    : (shortDescEn || null);

  // Collapsed text: short_description if available, else first 280 chars of full desc
  const collapsedText = shortDesc || (desc ? desc.slice(0, 280) + (desc.length > 280 ? '…' : '') : null);
  const displayText = expanded ? desc : collapsedText;

  // Show toggle if full desc is longer than the collapsed preview
  const showToggle = desc && (shortDesc ? desc !== shortDesc : desc.length > 280);

  // Нема ні опису ні категорій — не показуємо блок взагалі
  if (!displayText && categories.length === 0) return null;

  return (
    <div className="pdp-glass" style={GLASS}>
      <SecTitle>{t('pdp_about')}</SecTitle>

      {displayText && (
        <>
          <div className="pdp-desc-text" style={{
            fontSize: 13, lineHeight: 1.8, color: 'var(--v4-fg-2)',
            whiteSpace: 'pre-line',
          }}>
            {displayText}
          </div>

          {showToggle && (
            <button onClick={() => setExpanded(e => !e)} style={{
              display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
              fontSize: 12, fontWeight: 700, color: 'var(--gs-green)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}>
              <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }} />
              {expanded ? t('pdp_collapse') : t('pdp_expand')}
            </button>
          )}
        </>
      )}

      {categories.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: displayText ? 14 : 0 }}>
          {categories.map(c => (
            <span key={c.key} className="pdp-cat-tag" style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: 'var(--v4-panel-2)', border: '1px solid var(--v4-border)', color: 'var(--v4-fg-3)' }}>
              {lang === 'ru' ? c.label_ru : lang === 'uk' ? (c.label_ru || c.label_en) : c.label_en}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
